import {
  type AttributionRow,
  buildAttributionRows,
} from 'lib/attribution-display';

type AttributionDetailsProps = {
  data?: Record<string, unknown> | null;
  title?: string;
  leadSource?: string | null;
  emptyMessage?: string;
  sourceNote?: string | null;
};

function AttributionField({ row }: { row: AttributionRow }) {
  return (
    <div className={row.multiline ? 'sm:col-span-2' : undefined}>
      <dt className="text-sm font-medium text-gray-500">{row.label}</dt>
      <dd
        className={`mt-1 text-xs text-gray-700 ${
          row.multiline ? 'break-all whitespace-pre-wrap' : 'capitalize'
        }`}
      >
        {row.href ? (
          <a
            className="underline text-purple-700 break-all"
            target="_blank"
            rel="noreferrer"
            href={row.href}
          >
            {row.value}
          </a>
        ) : (
          row.value
        )}
      </dd>
    </div>
  );
}

export default function AttributionDetails({
  data,
  title = 'Marketing attribution',
  leadSource = null,
  emptyMessage = 'No marketing attribution was captured for this record.',
  sourceNote = null,
}: AttributionDetailsProps) {
  const rows = buildAttributionRows(data);

  if (rows.length === 0 && !leadSource) {
    return (
      <div className="sm:col-span-2">
        <dt className="text-sm font-medium text-gray-500 mb-2">{title}</dt>
        <dd className="text-sm text-gray-500">{emptyMessage}</dd>
      </div>
    );
  }

  return (
    <div className="sm:col-span-2">
      <dt className="text-sm font-medium text-gray-500 mb-4">{title}</dt>
      <dd className="mt-1">
        {sourceNote ? (
          <p className="mb-3 text-xs text-gray-600">{sourceNote}</p>
        ) : null}
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {leadSource ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                  Lead type
                </dt>
                <dd className="mt-1 text-sm text-gray-900 capitalize">
                  {leadSource.replace(/-/g, ' ')}
                </dd>
              </div>
            ) : null}
            {rows.map((row) => (
              <AttributionField key={`${row.label}-${row.value}`} row={row} />
            ))}
          </dl>
          {rows.length === 0 ? (
            <p className="text-sm text-gray-600">{emptyMessage}</p>
          ) : null}
        </div>
      </dd>
    </div>
  );
}
